import React, { useCallback, useEffect, useRef, useState } from 'react';

import { BreakpointMatch } from '@rover/react-lib/src/hooks/useMatchMedia';
import useNotificationsCenterShim from '@rover/shared/js/hooks/useNotificationsCenterShim';

import NotificationsCenter from './NotificationsCenter';
import usePreventBodyScroll from './usePreventBodyScroll';

type NotificationsCenterProps = React.ComponentProps<typeof NotificationsCenter>;

const useNotificationsCenterEffects = (
  isMobileBreakpoint: BreakpointMatch
): Pick<NotificationsCenterProps, 'handleButtonClick' | 'isOpen'> => {
  const { setNotificationsCenterHasOpened, reinitialize, fireNotificationCenterOpened } =
    useNotificationsCenterShim();
  const togglePreventBodyScroll = usePreventBodyScroll();

  const [isOpen, setIsOpen] = useState(false);

  const breakpointMatch = useRef(isMobileBreakpoint);

  const closeNotificationsCenter = useCallback(() => {
    if (isMobileBreakpoint) togglePreventBodyScroll(false);

    setIsOpen(false);
  }, [togglePreventBodyScroll, setIsOpen, isMobileBreakpoint]);

  // Orchestrates everything that happens around the dropdown opening & closing
  const toggleIsOpen = useCallback(() => {
    setNotificationsCenterHasOpened();
    if (!isOpen) {
      reinitialize();
      fireNotificationCenterOpened();
    }
    if (isMobileBreakpoint) togglePreventBodyScroll();

    setIsOpen(!isOpen);
  }, [
    setNotificationsCenterHasOpened,
    reinitialize,
    fireNotificationCenterOpened,
    isMobileBreakpoint,
    togglePreventBodyScroll,
    setIsOpen,
    isOpen,
  ]);

  // Closes the dropdown when crossing the media query boundary on resize
  useEffect(() => {
    if (
      isOpen &&
      breakpointMatch.current !== undefined &&
      isMobileBreakpoint !== undefined &&
      breakpointMatch.current !== isMobileBreakpoint
    ) {
      toggleIsOpen();
    }

    // Update ref so we hold the previous version of matchesMediaQuery
    breakpointMatch.current = isMobileBreakpoint;
  }, [isOpen, toggleIsOpen, breakpointMatch, isMobileBreakpoint, closeNotificationsCenter]);

  // Handles document event listener
  useEffect(() => {
    const eventListenerArgs = ['click', closeNotificationsCenter, false] as const;
    if (isOpen) {
      // we need the setTimeout to ensure the listener is added after the click is handled
      setTimeout(() => {
        document.addEventListener(...eventListenerArgs);
      }, 0);
    } else {
      document.removeEventListener(...eventListenerArgs);
    }
    // return cleanup function
    return () => document.removeEventListener(...eventListenerArgs);
  }, [isOpen, closeNotificationsCenter]);

  return { handleButtonClick: toggleIsOpen, isOpen };
};

export default useNotificationsCenterEffects;
